import { api } from './api';
import type { ApiResponse } from './api';
import type { UserFeedbackType } from '@safetyhub/shared';
import { useAuthStore } from '../stores/auth';

/**
 * AI 개선대책 피드백의 유형 — **`UserFeedbackType`(사용자 피드백)과 다른 축**이다.
 * 이쪽은 Prisma `AiFeedbackType`(값은 **바로 아래 union이 전수**: `MEASURE_*` 다섯)이고,
 * 저쪽은 `packages/shared/src/types/user-feedback.ts`의 `UserFeedbackType`이다
 * (그쪽이 정본이고 Prisma `enum UserFeedbackType`의 미러다). 이름이 비슷해 섞기 쉬우니
 * 합치지 말 것.
 *
 * ⚠️ **저쪽 값을 여기 옮겨 적지 말 것 — 적으면 낡는다.** 실제로 낡았다: 종전 주석이
 * `BUG_REPORT`·`FEATURE_REQUEST`·`GENERAL` 셋만 열거해 **같은 라운드에 추가된
 * `IMPROVEMENT`가 빠져** 있었다(2026-10-13 정정). 하필 「두 축을 섞지 말라」는 문장이라,
 * 빠진 값을 읽는 사람이 그것을 **또 다른 축**으로 읽게 만드는 자리였다.
 */
export type FeedbackType =
  | 'MEASURE_LEGAL_ADD'
  | 'MEASURE_LEGAL_REMOVE'
  | 'MEASURE_SUGGEST'
  | 'MEASURE_ADD'
  | 'MEASURE_REMOVE';

export interface CreateFeedbackParams {
  type: FeedbackType;
  category?: string;
  title?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  entityType?: string;
  entityId?: string;
}

/**
 * 피드백 제출 (백그라운드)
 * 실패해도 사용자 경험에 영향을 주지 않음
 */
export async function submitFeedback(params: CreateFeedbackParams): Promise<void> {
  try {
    await api.post('/feedback', params);
  } catch {
    // 피드백 제출 실패는 무시 (핵심 기능이 아님)
    console.debug('Feedback submission failed (ignored)');
  }
}

/**
 * 개선 대책 추가 피드백
 */
export function submitMeasureAddFeedback(
  measure: string,
  context: {
    category?: string;
    subCategory?: string;
    findingId?: string;
    inspectionId?: string;
  },
): void {
  submitFeedback({
    type: 'MEASURE_ADD',
    category: context.category,
    description: measure,
    metadata: {
      measure,
      subCategory: context.subCategory,
      findingId: context.findingId,
      inspectionId: context.inspectionId,
    },
    entityType: context.findingId ? 'finding' : undefined,
    entityId: context.findingId,
  });
}

// ============================================
// 사용자 피드백 (UserFeedback) — 유형 어휘는 shared `USER_FEEDBACK_TYPES`가 정본이다
// (화면 문구는 `USER_FEEDBACK_TYPE_LABELS`. 여기 한국어 문구를 적지 말 것 — 2026-10-12 리뷰 ①)
// ============================================

export interface SubmitUserFeedbackParams {
  /**
   * ⚠️ 종전 `type: string`이라 **enum과 tsc로 묶여 있지 않았다** — 서버는
   * `@IsEnum(UserFeedbackType)`로 받으므로 오타는 런타임 400으로만 드러났다(2026-10-12 리뷰 ①).
   */
  type: UserFeedbackType;
  title: string;
  description: string;
  diagnostics: string; // JSON string
  pageUrl?: string;
  screenshot?: Blob;
  annotation?: Blob;
  images?: File[];
}

/**
 * 사용자 피드백 제출
 * POST /api/v1/user-feedback (multipart/form-data)
 *
 * 귀속은 **제출 당시의 작업공간**(`authStore.currentCompanyId`)이다 — 결정 2026-10-10.
 * 종전엔 작업공간을 아예 보내지 않아 서버가 활성 멤버십 **첫 번째**로 귀속시켰고, 다중 소속자는
 * 보고 있던 조직과 무관한 조직에 붙었다.
 *
 * ⚠️ 호출부가 아니라 여기서 스토어를 읽는다 — 제출 경로가 늘어도 이 한 자리가 전부를 덮는다.
 * (`lib/api.ts`가 `stores/group`을 읽는 선례와 같은 층이고, `stores/auth → lib/api`는 단방향이라
 *  새 순환이 생기지 않는다.)
 * ⚠️ 값이 `null`이면 **필드를 싣지 않는다** — 빈 문자열을 보내면 서버의 허용 집합 대조에서
 * 400이 된다(`dto.companyId`가 truthy일 때만 검증 경로를 탄다).
 */
export async function submitUserFeedback(
  data: SubmitUserFeedbackParams,
): Promise<ApiResponse<unknown>> {
  const formData = new FormData();
  formData.append('type', data.type);
  formData.append('title', data.title);
  formData.append('description', data.description);
  formData.append('diagnostics', data.diagnostics);
  const companyId = useAuthStore.getState().currentCompanyId;
  if (companyId) {
    formData.append('companyId', companyId);
  }
  if (data.pageUrl) {
    formData.append('pageUrl', data.pageUrl);
  }

  if (data.screenshot) {
    formData.append('screenshot', data.screenshot, 'screenshot.jpg');
  }
  if (data.annotation) {
    formData.append('annotation', data.annotation, 'annotation.jpg');
  }
  if (data.images) {
    data.images.forEach((file, index) => {
      formData.append(`images`, file, file.name || `image-${index}.jpg`);
    });
  }

  const response = await api.post<ApiResponse<unknown>>('/user-feedback', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * 개선 대책 삭제 피드백
 */
export function submitMeasureRemoveFeedback(
  measure: string,
  context: {
    category?: string;
    subCategory?: string;
    findingId?: string;
    inspectionId?: string;
  },
): void {
  submitFeedback({
    type: 'MEASURE_REMOVE',
    category: context.category,
    description: measure,
    metadata: {
      measure,
      subCategory: context.subCategory,
      findingId: context.findingId,
      inspectionId: context.inspectionId,
    },
    entityType: context.findingId ? 'finding' : undefined,
    entityId: context.findingId,
  });
}
